import React, { useState } from 'react';
import { Dialog } from '../ui/radix/Dialog';
import { Button, Callout, Divider, Icon } from '../ui';
import { useMutation } from '@tanstack/react-query';
import apiTokenService, { CreateApiTokenData } from '../../services/apiTokenService';

interface CreateTokenDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (error: string) => void;
}

const AVAILABLE_ABILITIES = [
  { value: 'get_rates', label: 'Get Shipping Rates', description: 'Fetch shipping rates' },
  { value: 'create_shipment', label: 'Create Shipments', description: 'Create new shipments' },
  { value: '*', label: 'All Abilities', description: 'Full access to all API features' },
];

const CreateTokenDialog: React.FC<CreateTokenDialogProps> = ({ open, onClose, onSuccess, onError }) => {
  const [name, setName] = useState('');
  const [abilities, setAbilities] = useState<string[]>(['get_rates']);
  const [expiresAt, setExpiresAt] = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useMutation({
    mutationFn: apiTokenService.createToken,
    onSuccess: (data) => { setCreatedToken(data.token); },
    onError: (err) => {
      onError(err instanceof Error ? err.message : 'Failed to create token');
      handleClose();
    },
  });

  const handleAbilityChange = (ability: string) => {
    if (ability === '*') {
      setAbilities(abilities.includes('*') ? [] : ['*']);
    } else {
      const newAbilities = abilities.includes(ability)
        ? abilities.filter((a) => a !== ability)
        : [...abilities.filter((a) => a !== '*'), ability];
      setAbilities(newAbilities);
    }
  };

  const handleCreate = () => {
    if (!name.trim()) { onError('Token name is required'); return; }
    if (abilities.length === 0) { onError('Please select at least one ability'); return; }
    const data: CreateApiTokenData = { name: name.trim(), abilities, expires_at: expiresAt || null };
    createMutation.mutate(data);
  };

  const handleCopyToken = () => {
    if (createdToken) {
      navigator.clipboard.writeText(createdToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    if (createdToken) onSuccess('API token created successfully');
    setName('');
    setAbilities(['get_rates']);
    setExpiresAt('');
    setCreatedToken(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) (createdToken ? handleClose : onClose)(); }}
      title={createdToken ? 'API Token Created' : 'Create API Token'}
      maxWidth={560}
      footer={
        createdToken ? (
          <Button variant="primary" block onClick={handleClose}>Done</Button>
        ) : (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={onClose} disabled={createMutation.isPending}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={createMutation.isPending || !name.trim() || abilities.length === 0}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Token'}
            </Button>
          </div>
        )
      }
    >
      {createdToken ? (
        <div>
          <Callout tone="warn" style={{ marginBottom: 20, fontSize: 13 }}>
            <Icon name="alertCircle" size={14} style={{ marginRight: 6 }} />
            Make sure to copy your API token now. You won't be able to see it again!
          </Callout>

          <div style={{ padding: 14, background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 8, marginBottom: 20 }}>
            <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-3)', marginBottom: 8 }}>API Token</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                className="input"
                value={createdToken}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: 13, flex: 1 }}
              />
              <Button variant="primary" size="sm" onClick={handleCopyToken} style={{ flexShrink: 0 }}>
                <Icon name="copy" size={14} style={{ marginRight: 6 }} />
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>

          <Divider style={{ marginBottom: 16 }} />

          <p className="muted" style={{ fontSize: 12, marginBottom: 8 }}>Use this token in your API requests:</p>
          <pre style={{ padding: 12, background: '#1a1a2e', color: '#e2e8f0', borderRadius: 8, fontSize: 12, overflowX: 'auto', margin: 0 }}>
            Authorization: Bearer {createdToken}
          </pre>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 6 }}>
              Token Name
            </label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Integration"
            />
            <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>A descriptive name to identify this token</p>
          </div>

          <div>
            <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 10 }}>Abilities</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {AVAILABLE_ABILITIES.map((ability) => (
                <label
                  key={ability.value}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={abilities.includes(ability.value)}
                    onChange={() => handleAbilityChange(ability.value)}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{ability.label}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{ability.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 6 }}>
              Expiration Date (Optional)
            </label>
            <input
              className="input"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
            <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>Leave empty for no expiration</p>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default CreateTokenDialog;
